import React, { useEffect, useState } from "react";
import { Download, X, Mail, Trash2, AlertTriangle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = [["new", "Neu"], ["review", "In Prüfung"], ["interview", "Interview geplant"], ["offer", "Angebot"], ["rejected", "Abgesagt"], ["hired", "Eingestellt"]];
const STYLE = { new: "bg-blue-100 text-blue-700", review: "bg-amber-100 text-amber-700", interview: "bg-purple-100 text-purple-700", offer: "bg-teal-100 text-teal-700", rejected: "bg-red-50 text-red-500", hired: "bg-green-100 text-green-700" };

export function CareersApplications() {
  const [data, setData] = useState({ applications: [], labels: {}, pending_deletion_count: 0 });
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ status: "", branch_id: "", since: "" });
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    api.get(`/admin/careers/applications?${params.toString()}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [filters]);
  useEffect(() => { api.get("/careers/meta").then((r) => setBranches(r.data.branches || [])); }, []);

  const downloadFile = async (appId, f) => {
    try {
      const res = await api.get(`/admin/careers/applications/${appId}/file/${f.file_id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const inp = "px-3 py-2 rounded-lg border border-gray-200 text-sm";
  return (
    <div>
      {data.pending_deletion_count > 0 && (
        <div className="mb-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800" data-testid="retention-banner">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span className="text-sm">{data.pending_deletion_count} Bewerbung(en) sind zur DSGVO-Löschung vorgemerkt (älter als 6 Monate, abgesagt). Bitte prüfen und Löschung bestätigen.</span>
          <button onClick={() => setFilters((f) => ({ ...f, status: "rejected" }))} className="ml-auto text-sm font-semibold underline whitespace-nowrap">Anzeigen</button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-5" data-testid="app-filters">
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inp} data-testid="app-filter-status">
          <option value="">Alle Status</option>
          {STATUSES.map((s) => <option key={s[0]} value={s[0]}>{s[1]}</option>)}
        </select>
        <select value={filters.branch_id} onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))} className={inp} data-testid="app-filter-branch">
          <option value="">Alle Standorte</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} className={inp} data-testid="app-filter-since" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr>
            <th className="p-3">Name</th><th className="p-3 hidden md:table-cell">Stelle</th><th className="p-3 hidden lg:table-cell">Standort</th><th className="p-3">Status</th><th className="p-3 hidden md:table-cell">Eingang</th>
          </tr></thead>
          <tbody>
            {data.applications.map((a) => (
              <tr key={a.id} onClick={() => setDetail(a)} className={`border-t border-gray-50 hover:bg-gray-50 cursor-pointer ${a.pending_deletion ? "bg-amber-50/50" : ""}`} data-testid={`app-row-${a.id}`}>
                <td className="p-3 font-medium">{a.first_name} {a.last_name}{a.pending_deletion && <span className="ml-2 text-xs text-amber-600">⏳ zur Löschung</span>}</td>
                <td className="p-3 hidden md:table-cell text-gray-600">{a.job_title}</td>
                <td className="p-3 hidden lg:table-cell text-gray-600">{a.desired_branch_name || "-"}</td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${STYLE[a.status]}`}>{data.labels[a.status]}</span></td>
                <td className="p-3 hidden md:table-cell text-gray-500">{(a.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {!data.applications.length && <tr><td colSpan={5} className="p-8 text-center text-gray-400">Keine Bewerbungen</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && <AppDetail app={detail} labels={data.labels} onClose={() => setDetail(null)} onChange={() => { load(); }} onDownload={downloadFile} />}
    </div>
  );
}

function AppDetail({ app, labels, onClose, onChange, onDownload }) {
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState(app.internal_notes || "");
  const [contact, setContact] = useState(null); // {template}
  const [msg, setMsg] = useState("");

  const save = async () => {
    await api.put(`/admin/careers/applications/${app.id}`, { status, internal_notes: notes });
    toast.success("Aktualisiert"); onChange();
  };
  const sendContact = async () => {
    try { await api.post(`/admin/careers/applications/${app.id}/contact`, { template: contact.template, message: msg });
      toast.success("E-Mail gesendet"); setContact(null); setMsg("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    if (!window.confirm("Bewerbung endgültig löschen? Ein DSGVO-Löschprotokoll bleibt erhalten.")) return;
    await api.post(`/admin/careers/applications/${app.id}/confirm-delete`);
    toast.success("Gelöscht (protokolliert)"); onChange(); onClose();
  };
  const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 text-sm";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="app-detail">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">{app.first_name} {app.last_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-1 text-sm text-gray-600">
          <p><strong>Stelle:</strong> {app.job_title}</p>
          <p><strong>Standort:</strong> {app.desired_branch_name || "-"}</p>
          <p><strong>E-Mail:</strong> <a href={`mailto:${app.email}`} className="text-[#D62B3A]">{app.email}</a></p>
          <p><strong>Telefon:</strong> {app.phone}</p>
          <p><strong>Eingang:</strong> {(app.created_at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
        {app.message && <div className="mt-3 bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-line">{app.message}</div>}

        {app.files?.length > 0 && (
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Anhänge</label>
            <div className="mt-1 space-y-1">
              {app.files.map((f) => (
                <button key={f.file_id} onClick={() => onDownload(app.id, f)} className="w-full flex items-center gap-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 text-left" data-testid={`app-file-${f.file_id}`}>
                  <Download className="w-4 h-4 text-[#D62B3A]" /> {f.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inp} mt-1`} data-testid="app-status-select">
              {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-sm font-medium text-gray-700">Interne Notizen</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inp} mt-1`} data-testid="app-notes" />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={save} className="flex-1 py-2.5 rounded-xl bg-[#D62B3A] text-white font-semibold text-sm" data-testid="app-save">Speichern</button>
          <button onClick={() => setContact({ template: "invite" })} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="app-invite"><Mail className="w-4 h-4" /> Einladen</button>
          <button onClick={() => setContact({ template: "reject" })} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="app-reject"><Mail className="w-4 h-4" /> Absagen</button>
        </div>

        {contact && (
          <div className="mt-4 bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-medium">{contact.template === "invite" ? "Einladung zum Gespräch" : contact.template === "reject" ? "Höfliche Absage" : "Nachricht"}</p>
            <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Optionaler persönlicher Zusatztext (eine Vorlage im HELIUM-Ton wird automatisch verwendet)…" className={`${inp} mt-2`} data-testid="app-contact-msg" />
            <div className="flex gap-2 mt-2">
              <button onClick={sendContact} className="px-4 py-2 rounded-lg bg-[#D62B3A] text-white text-sm font-semibold" data-testid="app-contact-send">Senden</button>
              <button onClick={() => setContact(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm">Abbrechen</button>
            </div>
          </div>
        )}

        {(app.pending_deletion || app.deletion_requested) && (
          <button onClick={confirmDelete} className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-50 text-red-600 font-semibold text-sm" data-testid="app-confirm-delete">
            <Trash2 className="w-4 h-4" /> Löschung bestätigen (DSGVO)
          </button>
        )}
      </div>
    </div>
  );
}
